/**
 * Test account roles — dedicated QA roles for controlled journey testing.
 *
 * Test_Consumer mirrors Consumer_User; Test_Trade mirrors Trade_Business.
 * They are deliberately distinct from production roles: they must never be
 * offered in public registration, onboarding, invitations, pricing or public
 * profile labels, and live billing / referrals / reviews / publishing are
 * blocked for them at the database layer (guard_test_account_side_effects).
 *
 * Assignment is restricted to Staff_Super_Admin (enforced by the
 * guard_test_role_assignment trigger on user_roles).
 */
import type { AppRole } from "@/lib/roles";

export const TEST_ROLES = ["Test_Consumer", "Test_Trade"] as const;
export type TestRole = (typeof TEST_ROLES)[number];

export function isTestRole(role: string): role is TestRole {
  return role === "Test_Consumer" || role === "Test_Trade";
}

/** True when any active role marks the account as a test account. */
export function hasTestRole(roles: readonly string[]): boolean {
  return roles.some(isTestRole);
}

/**
 * Roles safe to show on public surfaces. Test roles are stripped so test
 * accounts never surface a "Test" label on public profiles.
 */
export function publicRoles<T extends string>(roles: readonly T[]): T[] {
  return roles.filter((r) => !isTestRole(r));
}

/** Fallback landing route for a test account with no production context. */
export function testRoleLandingRoute(roles: readonly AppRole[] | undefined | null): string | null {
  const list = roles ?? [];
  if (list.includes("Test_Trade")) return "/trade/dashboard";
  if (list.includes("Test_Consumer")) return "/app/home";
  return null;
}

